<template>
  <div v-if="!hidden" class="flex-none w-56">
    <!-- Rating badge (sits where the date marker does on dashboard cards). -->
    <p class="text-highlight text-[11px] tracking-wide mb-2 h-4 truncate">
      {{ ratingsText }}
    </p>

    <!-- Backdrop -->
    <div
      class="group relative w-full aspect-video rounded-lg overflow-hidden bg-primary-500 border border-primary-400"
    >
      <img
        v-if="image"
        :src="image"
        class="h-full w-full object-cover"
        :alt="series.name"
      />

      <!-- Track. Always visible on touch screens (which have no hover) and
           revealed on hover/focus on pointer devices. -->
      <button
        type="button"
        class="absolute top-2 right-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary-700/80 text-highlight transition hover:bg-primary-700"
        :class="revealClass"
        :disabled="loading || tracked || !canTrackMore"
        :aria-label="trackLabel"
        :title="trackLabel"
        @click.prevent="track"
      >
        <Font-awesome-icon
          :icon="['fas', tracked ? 'check' : !canTrackMore ? 'lock' : 'plus']"
        />
      </button>
    </div>

    <!-- Meta (centered) -->
    <div class="pt-3 text-center">
      <p class="text-white text-sm truncate">
        {{ series.name }}
        <span v-if="year" class="text-muted">({{ year }})</span>
      </p>
      <p v-if="series.seasons" class="text-xs mt-1">
        <span class="text-subtle opacity-80">{{ seasonsText }}</span>
        <span
          class="ml-1"
          :class="series.ongoing ? 'text-highlight' : 'text-subtle opacity-80'"
        >
          · {{ series.ongoing ? $t("explore.ongoing") : $t("explore.ended") }}
        </span>
      </p>

      <!-- Vote -->
      <div class="mt-1 flex items-center justify-center gap-1">
        <button
          v-for="opt in voteOptions"
          :key="opt.value"
          type="button"
          class="flex h-10 w-10 items-center justify-center rounded-md transition disabled:cursor-wait"
          :class="
            vote === opt.value
              ? 'text-highlight'
              : 'text-muted opacity-75 hover:opacity-100'
          "
          :disabled="voting"
          :aria-pressed="vote === opt.value ? 'true' : 'false'"
          :aria-label="$t('vote.' + opt.value)"
          :title="$t('vote.' + opt.value)"
          @click.prevent="setVote(opt.value)"
        >
          <Font-awesome-icon
            :icon="[vote === opt.value ? 'fas' : 'far', opt.icon]"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { seriesYear, seriesImage } from "@/utils/series";

export default {
  name: "RecommendationCard",
  props: {
    series: { required: true },
  },

  data() {
    return {
      loading: false,
      tracked: false,
      vote: this.series.vote ?? null,
      voting: false,
      hidden: false,
      voteOptions: [
        { value: "dislike", icon: "thumbs-down" },
        { value: "like", icon: "thumbs-up" },
        { value: "really_like", icon: "heart" },
      ],
    };
  },

  computed: {
    /** Hover-to-reveal only where hover exists; touch screens always show it. */
    revealClass() {
      if (this.tracked || this.loading) return "opacity-100";
      return "opacity-100 lg:opacity-0 lg:group-hover:opacity-100 lg:focus-visible:opacity-100";
    },
    canTrackMore() {
      return this.$store.getters.canTrackMore;
    },
    trackLabel() {
      if (this.tracked) return this.$t("explore.tracked");
      if (!this.canTrackMore) {
        return this.$t("account.freeLimitReached", {
          count: this.$store.getters.seriesLimit,
        });
      }
      return this.$t("explore.track");
    },
    image() {
      return seriesImage(this.series, "backdrop");
    },
    year() {
      return seriesYear(this.series);
    },
    ratingsText() {
      return (this.series.ratings || [])
        .map(
          (r) =>
            `${r.source === "Rotten Tomatoes" ? "RT" : r.source} ${r.value}`,
        )
        .join("   ·   ");
    },
    seasonsText() {
      return this.$tc("explore.seasons", this.series.seasons, {
        count: this.series.seasons,
      });
    },
  },

  methods: {
    track() {
      this.loading = true;
      this.$store
        .dispatch("track", this.series.id)
        .then(() => (this.tracked = true))
        .catch(() => undefined)
        .finally(() => (this.loading = false));
    },
    setVote(value) {
      // Clicking the active vote again clears it.
      const next = this.vote === value ? null : value;
      this.voting = true;
      this.$store
        .dispatch("vote", { seriesId: this.series.id, vote: next })
        .then(() => {
          this.vote = next;
          // A dislike removes the card — it won't be recommended again.
          if (next === "dislike") this.hidden = true;
        })
        .catch(() => undefined)
        .finally(() => (this.voting = false));
    },
  },
};
</script>
